// =============================================================================
// MeuDriver — Batch O4: AdminPayouts
// Painel de repasses e financeiro para administradores — /admin/pagamentos.
// O histórico de saques do sistema antigo (payout_requests) saiu em 2026-10-11:
// a tabela estava vazia e o repasse agora é por chave Pix (AdminPayoutsDueCard).
// =============================================================================

import { DollarSign, TrendingUp, Clock, CheckCircle2, Users } from 'lucide-react'
import { useAdminFinancialSummary } from '@/hooks/useAdminPayouts'
import { formatCurrency } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { PLATFORM_FEE_PERCENT } from '@/lib/constants'
import { AdminPayoutsDueCard } from '@/components/admin/AdminPayoutsDueCard'

// ---------------------------------------------------------------------------
// KPI Cards
// ---------------------------------------------------------------------------

interface KpiCardProps {
  label: string
  value: string
  icon: React.ReactNode
  className?: string
}

function KpiCard({ label, value, icon, className = '' }: KpiCardProps) {
  return (
    <Card className={className}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-muted-foreground font-medium">{label}</p>
          <div className="h-9 w-9 rounded-xl bg-brand-50 flex items-center justify-center">
            {icon}
          </div>
        </div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// AdminPayouts — página principal
// ---------------------------------------------------------------------------

export default function AdminPayouts() {
  const { data: summary, isLoading: summaryLoading } = useAdminFinancialSummary()

  const kpis = [
    {
      label: 'Volume movimentado',
      value: summaryLoading ? '...' : formatCurrency(summary?.total_gmv ?? 0),
      icon: <TrendingUp className="h-4 w-4 text-brand-600" />,
    },
    {
      label: `Taxa da plataforma (+${PLATFORM_FEE_PERCENT}% sobre o instrutor)`,
      value: summaryLoading ? '...' : formatCurrency(summary?.total_commission ?? 0),
      icon: <DollarSign className="h-4 w-4 text-brand-600" />,
    },
    {
      label: 'Total repassado',
      value: summaryLoading ? '...' : formatCurrency(summary?.total_paid_out ?? 0),
      icon: <CheckCircle2 className="h-4 w-4 text-green-600" />,
    },
    {
      label: 'Repasses pendentes',
      value: summaryLoading ? '...' : formatCurrency(summary?.pending_payouts ?? 0),
      icon: <Clock className="h-4 w-4 text-amber-600" />,
    },
    {
      label: 'Saldo da plataforma',
      value: summaryLoading ? '...' : formatCurrency(summary?.platform_balance ?? 0),
      icon: <Users className="h-4 w-4 text-brand-600" />,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-brand-600" />
          Repasses aos instrutores
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          O que cada instrutor tem a receber e o registro do que já foi pago.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpis.map((kpi) => (
          <KpiCard
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            icon={kpi.icon}
          />
        ))}
      </div>

      {/* Repasses devidos (instructor_earnings) — pagamento por Pix */}
      <AdminPayoutsDueCard />
    </div>
  )
}
